import { Box, Typography, Button, useTheme, alpha } from "@mui/material";
import { Refresh, WavingHand } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { memo, useMemo } from "react";

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const getFormattedDate = () => {
  return new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
};

const HomeHeader = memo(({ selectedOrganisation, onRefresh, userName }) => {
  const theme = useTheme();
  const { t } = useTranslation("common");

  const greeting = useMemo(() => getGreeting(), []);
  const formattedDate = useMemo(() => getFormattedDate(), []);

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        borderRadius: 3,
        p: 3,
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.15)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.15)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        backdropFilter: "blur(10px)",
        border: `1px solid ${
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.1)"
            : "rgba(0,0,0,0.08)"
        }`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative gradient orb */}
      <Box
        sx={{
          position: "absolute",
          top: -50,
          right: -50,
          width: 150,
          height: 150,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
          pointerEvents: "none",
        }}
      />

      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <WavingHand
            sx={{
              fontSize: 20,
              color: theme.palette.warning.main,
            }}
          />
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {greeting}
            {userName ? `, ${userName.split(" ")[0]}` : ""}
          </Typography>
        </Box>
        <Typography
          variant="h4"
          component="h1"
          fontWeight={700}
          sx={{
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${theme.palette.common.white} 0%, ${theme.palette.grey[400]} 100%)`
                : `linear-gradient(135deg, ${theme.palette.grey[900]} 0%, ${theme.palette.grey[700]} 100%)`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 0.5,
          }}
        >
          {selectedOrganisation?.name || "Dashboard"}
        </Typography>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Typography variant="body2" color="text.secondary">
            {formattedDate}
          </Typography>
        </Box>
      </Box>

      <Button
        variant="outlined"
        startIcon={<Refresh />}
        onClick={onRefresh}
        sx={{
          borderRadius: 2,
          px: 3,
          py: 1.5,
          borderColor: alpha(theme.palette.primary.main, 0.5),
          color: theme.palette.primary.main,
          "&:hover": {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        {t("common.refresh")}
      </Button>
    </Box>
  );
});

HomeHeader.displayName = "HomeHeader";

export default HomeHeader;
